import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { APP_VERSION, APP_VERSION_DATE } from '../version';
import { ENTRADAS, COLORES_TIPO, ICONOS_TIPO } from '../bitacoraEntries';
import { useAuth } from '../context/AuthContext';

// ============================================================================
// NovedadesVersionModal — Popup que muestra las entradas de la VERSIÓN ACTUAL.
//
// Se abre al hacer clic en la versión (v1.5.0) al lado del logo "IMEC".
// Visible para TODOS los usuarios autenticados. Si el usuario es admin, se
// muestra adicionalmente un botón "Ver bitácora completa →" que lleva a
// /bitacora#v{APP_VERSION} (la página completa solo es accesible para admin).
// ============================================================================

const formatearFechaLarga = (iso) => {
  const meses = ['enero','febrero','marzo','abril','mayo','junio',
                 'julio','agosto','septiembre','octubre','noviembre','diciembre'];
  const [y, m, d] = iso.split('-');
  return `${parseInt(d, 10)} de ${meses[parseInt(m, 10) - 1]} de ${y}`;
};

const NovedadesVersionModal = ({ isOpen, onClose }) => {
  const { isAdmin } = useAuth();

  // Cerrar con ESC
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEsc);
    // Evitar scroll del body cuando el modal está abierto
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Filtrar entradas de la versión actual
  const entradasVersion = useMemo(() => {
    return ENTRADAS.filter(e => e.version === APP_VERSION);
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header del modal con gradiente */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-5 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-3xl">🎉</span>
              <h2 className="text-2xl font-bold">Novedades en v{APP_VERSION}</h2>
            </div>
            <p className="text-sm opacity-90">
              Publicada el {formatearFechaLarga(APP_VERSION_DATE)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white text-2xl leading-none p-1 rounded hover:bg-white/10 transition"
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        {/* Cuerpo scrolleable con las entradas */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {entradasVersion.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No hay registro de cambios para esta versión.
            </div>
          ) : (
            entradasVersion.map((entrada, idx) => {
              const c = COLORES_TIPO[entrada.tipo] || COLORES_TIPO.chore;
              return (
                <div
                  key={idx}
                  className={`bg-gray-50 rounded-xl border-l-4 ${c.border} p-4`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl flex-shrink-0">{ICONOS_TIPO[entrada.tipo]}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-semibold text-gray-900">{entrada.titulo}</h3>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${c.bg} ${c.text}`}>
                          {c.label}
                        </span>
                      </div>
                      <ul className="space-y-1 text-sm text-gray-700">
                        {entrada.cambios.map((cambio, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="text-gray-400 flex-shrink-0">•</span>
                            <span>{cambio}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer del modal */}
        <div className="border-t border-gray-200 p-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition"
          >
            Cerrar
          </button>
          {isAdmin && (
            <Link
              to={`/bitacora#v${APP_VERSION}`}
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition text-center"
            >
              📖 Ver bitácora completa →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default NovedadesVersionModal;
